/**
 * Route Error Boundary
 *
 * A specialized error boundary for lazy-loaded routes. Catches chunk loading
 * failures, runtime errors, and other issues that occur when loading route
 * components. Renders a branded, full-page error fallback.
 *
 * Resets automatically on navigation by using React Router's location as a key.
 *
 * @module components/ErrorBoundary/RouteErrorBoundary
 */

import React, { Component } from 'react';
import { errorReporter, classifyError, ErrorType } from '../../utils/errorReporting';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional key that triggers a reset when it changes (e.g. location.pathname) */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Detect whether an error is a chunk/dynamic-import loading failure.
 */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    (error.name === 'ChunkLoadError')
  );
}

const isDev =
  typeof import.meta !== 'undefined' &&
  (import.meta as unknown as Record<string, unknown>).env != null &&
  !!(import.meta as unknown as { env: { DEV?: boolean } }).env.DEV;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Error boundary tailored for lazy-loaded route components.
 *
 * - Detects chunk-load failures and shows a "new version available" message.
 * - Reports errors to the configured error reporter (Sentry when enabled).
 * - Logs to console in development.
 * - Resets on navigation when `resetKey` changes.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log in development
    if (isDev) {
      console.error('[RouteErrorBoundary]', error, errorInfo);
    }

    // Report via unified error reporter (delegates to Sentry when configured)
    const appError = classifyError(error, ErrorType.RENDER_ERROR);
    appError.context = {
      ...appError.context,
      componentStack: errorInfo.componentStack,
      isChunkError: isChunkLoadError(error),
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };
    errorReporter.captureError(appError);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    // Reset the boundary when the route changes (resetKey)
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = (): void => {
    // For chunk errors, bust the cache to avoid re-fetching the same stale chunk
    if (this.state.isChunkError) {
      const url = new URL(window.location.href);
      url.searchParams.set('_cb', Date.now().toString());
      window.location.href = url.toString();
    } else {
      window.location.reload();
    }
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { isChunkError } = this.state;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-screen bg-surface-dark flex items-center justify-center px-4"
      >
        <div className="max-w-md w-full text-center">
          {/* WritersLogic logo mark */}
          <div className="flex justify-center mb-6">
            <svg
              width={48}
              height={48}
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="routeEbGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22C55E" />
                  <stop offset="50%" stopColor="#EAB308" />
                  <stop offset="100%" stopColor="#EF4444" />
                </linearGradient>
              </defs>
              <path
                d="M4 16 L8 24 L12 10 L16 20 L20 6 L24 18 L28 12"
                fill="none"
                stroke="url(#routeEbGrad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Headline */}
          <h1 className="text-xl font-semibold text-white mb-2">
            {isChunkError ? 'Page failed to load' : 'Something went wrong'}
          </h1>

          {/* Subtext */}
          <p className="text-sm text-gray-400 mb-8 leading-relaxed">
            {isChunkError
              ? 'This page couldn\u2019t be loaded \u2014 this can happen after an update or on a slow connection. Reloading should fix it.'
              : 'We hit an unexpected error. This has been reported automatically.'}
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-surface-dark"
              style={{ backgroundColor: 'hsl(220, 60%, 50%)' }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = 'hsl(220, 60%, 45%)')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'hsl(220, 60%, 50%)')
              }
            >
              Reload Page
            </button>

            {!isChunkError && (
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  this.handleGoHome();
                }}
                className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-gray-300 rounded-lg border border-gray-700 hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-surface-dark"
              >
                Go Home
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
